import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  AccessTime as AccessTimeIcon,
  Close as CloseIcon,
  DirectionsCar as DirectionsCarIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Sync as SyncIcon,
  Warning as WarningIcon,
  Work as WorkIcon
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';

const STATUS_LABELS = {
  all: 'All',
  on_job: 'On Job',
  driving: 'Driving',
  clocked_in_no_job: 'No Job',
  clocked_out_today: 'Clocked Out',
  job_time_only: 'Job Time Only'
};

const STATUS_COLORS = {
  on_job: 'primary',
  driving: 'default',
  clocked_in_no_job: 'secondary',
  clocked_out_today: 'default',
  job_time_only: 'default'
};

const ASPIRE_WALL_CLOCK_FORMAT = new Intl.DateTimeFormat(undefined, {
  timeZone: 'UTC',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
});

const ASPIRE_WALL_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  timeZone: 'UTC',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric'
});

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    background: '#f5f7f8',
    padding: theme.spacing(3)
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  toolbar: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 8
  },
  metric: {
    padding: theme.spacing(2),
    borderRadius: 8,
    minHeight: 108
  },
  metricValue: {
    fontSize: '1.8rem',
    fontWeight: 700,
    lineHeight: 1.1
  },
  muted: {
    color: theme.palette.text.secondary
  },
  tablePaper: {
    borderRadius: 8,
    overflow: 'hidden'
  },
  tableHead: {
    background: '#28363b',
    '& .MuiTableCell-head': {
      color: '#fff',
      fontWeight: 600
    }
  },
  rowButton: {
    cursor: 'pointer'
  },
  warningChip: {
    marginRight: theme.spacing(0.5),
    marginTop: theme.spacing(0.5)
  },
  drawer: {
    width: 520,
    maxWidth: '100vw',
    padding: theme.spacing(3)
  },
  detailBlock: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    background: '#f7f8f9',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: 12
  }
}));

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return ASPIRE_WALL_CLOCK_FORMAT.format(date);
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return ASPIRE_WALL_DATE_FORMAT.format(date);
}

function formatActualSyncTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

function formatElapsed(minutes) {
  if (minutes === null || minutes === undefined) return '-';
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hrs ? `${hrs}h ${mins}m` : `${mins}m`;
}

function getClockHours(row) {
  if (Number.isFinite(Number(row?.clockTime?.totalHours))) return Number(row.clockTime.totalHours);
  const start = row?.clockTime?.clockStart ? new Date(row.clockTime.clockStart).getTime() : null;
  const end = row?.clockTime?.clockEnd ? new Date(row.clockTime.clockEnd).getTime() : null;
  if (!start || !end) return null;
  return Math.max(0, (end - start) / 3600000);
}

function formatHours(hours) {
  if (!Number.isFinite(Number(hours))) return '-';
  return `${Number(hours).toFixed(2)}h`;
}

function getArizonaDateKey(date = new Date()) {
  const arizonaNow = new Date(date.getTime() - (7 * 60 * 60 * 1000));
  return arizonaNow.toISOString().slice(0, 10);
}

function Metric({ icon, label, value, helper }) {
  const classes = useStyles();
  return (
    <Paper className={classes.metric} elevation={1}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" color="textSecondary">{label}</Typography>
        {icon}
      </Box>
      <Typography className={classes.metricValue}>{value}</Typography>
      <Typography variant="caption" color="textSecondary">{helper}</Typography>
    </Paper>
  );
}

const AspireTimeclockDashboard = () => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [routeId, setRouteId] = useState('');
  const [businessDate, setBusinessDate] = useState(() => getArizonaDateKey());
  const [selectedRow, setSelectedRow] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (search.trim()) params.search = search.trim();
      if (routeId) params.routeId = routeId;
      if (businessDate) params.date = businessDate;
      const response = await axios.get('/api/aspire/timeclock/today', { params });
      setData(response.data);
    } catch (error) {
      setSnackbar({ open: true, severity: 'error', message: error.response?.data?.error || error.message || 'Failed to load Aspire timeclock' });
    } finally {
      setLoading(false);
    }
  }, [businessDate, routeId, search, status]);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, 30000);
    return () => clearInterval(timer);
  }, [fetchData]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      await axios.post('/api/aspire/timeclock/sync-now');
      setSnackbar({ open: true, severity: 'success', message: 'Aspire timeclock sync completed' });
      await fetchData();
    } catch (error) {
      setSnackbar({ open: true, severity: 'error', message: error.response?.data?.error || error.message || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const routeOptions = useMemo(() => {
    const routes = new Map();
    (data?.rows || []).forEach(row => {
      if (row.route?.routeId) routes.set(row.route.routeId, row.route.routeName || `Route ${row.route.routeId}`);
    });
    return Array.from(routes.entries()).sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [data]);

  const rows = data?.rows || [];
  const summary = data?.summary || {};
  const sync = data?.meta?.sync || {};
  const isViewingToday = data?.meta?.isToday !== false;
  const dateLabel = data?.meta?.businessDate || businessDate;

  return (
    <div className={classes.root}>
      <Container maxWidth={false}>
        <Box className={classes.header}>
          <Box>
            <Typography variant="h4">Aspire Timeclock</Typography>
            <Typography variant="body2" className={classes.muted}>
              {data?.meta ? `${dateLabel} business day (${formatDate(data.meta.businessDateStart)})` : 'Loading'}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gridGap={8}>
            {sync.stale && (
              <Chip
                icon={<WarningIcon />}
                color="secondary"
                label={sync.lastSuccessfulAt ? `Stale ${sync.ageSeconds}s` : 'No sync yet'}
              />
            )}
            <Tooltip title="Reload dashboard data">
              <span>
                <IconButton onClick={fetchData} disabled={loading}>
                  {loading ? <CircularProgress size={22} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={syncNow}
              disabled={syncing || !isViewingToday}
            >
              Sync Today
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <Metric icon={<PersonIcon />} label={isViewingToday ? 'Clocked In' : 'People'} value={isViewingToday ? (summary.clockedIn || 0) : (summary.totalRows || 0)} helper={isViewingToday ? `${summary.totalRows || 0} people in feed` : 'Clock rows for selected day'} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Metric icon={<WorkIcon />} label={isViewingToday ? 'On Job' : 'Job Rows'} value={summary.onJob || 0} helper={isViewingToday ? 'Active job time rows' : 'Rows with job context'} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Metric icon={<DirectionsCarIcon />} label="Driving" value={summary.driving || 0} helper="Active drive time rows" />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Metric icon={<WarningIcon />} label="No Job" value={summary.clockedInNoJob || 0} helper="Clocked in without active job" />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Metric icon={<WarningIcon />} label="Unmatched" value={summary.unmatchedContacts || 0} helper="No local employee match" />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Metric icon={<AccessTimeIcon />} label="Last Sync" value={sync.ageSeconds === null || sync.ageSeconds === undefined ? '-' : `${sync.ageSeconds}s`} helper={sync.lastSuccessfulAt ? formatActualSyncTime(sync.lastSuccessfulAt) : 'Never'} />
          </Grid>
        </Grid>

        <Paper className={classes.toolbar} elevation={1}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="disabled" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Date"
                type="date"
                value={businessDate}
                onChange={(event) => {
                  setBusinessDate(event.target.value);
                  setRouteId('');
                  setStatus('all');
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth variant="outlined" size="small" disabled={!isViewingToday}>
                <InputLabel>Status</InputLabel>
                <Select value={status} onChange={(event) => setStatus(event.target.value)} label="Status">
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Route</InputLabel>
                <Select value={routeId} onChange={(event) => setRouteId(event.target.value)} label="Route">
                  <MenuItem value="">All Routes</MenuItem>
                  {routeOptions.map(([id, label]) => (
                    <MenuItem key={id} value={id}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gridGap={8}>
                <Typography variant="body2" className={classes.muted}>
                  {rows.length} rows
                </Typography>
                <Button size="small" onClick={() => { setBusinessDate(getArizonaDateKey()); setRouteId(''); }}>
                  Today
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Paper className={classes.tablePaper} elevation={1}>
          <TableContainer>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>{isViewingToday ? 'Status' : 'Hours'}</TableCell>
                  <TableCell>Clock</TableCell>
                  <TableCell>Route / Crew</TableCell>
                  <TableCell>Job</TableCell>
                  <TableCell>Warnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && !rows.length ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box p={4} textAlign="center"><CircularProgress /></Box>
                    </TableCell>
                  </TableRow>
                ) : rows.length ? rows.map(row => (
                  <TableRow key={`${row.contactId}:${row.clockTime?.clockTimeId || row.workTicketTime?.workTicketTimeId || 'row'}`} hover className={classes.rowButton} onClick={() => setSelectedRow(row)}>
                    <TableCell>
                      <Typography variant="body2">{row.employee?.displayName || row.contactName}</Typography>
                      <Typography variant="caption" className={classes.muted}>Aspire Contact {row.contactId}</Typography>
                    </TableCell>
                    <TableCell>
                      {isViewingToday ? (
                        <Chip size="small" color={STATUS_COLORS[row.status] || 'default'} label={STATUS_LABELS[row.status] || row.status} />
                      ) : (
                        <Typography variant="body2">{formatHours(getClockHours(row))}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">In {formatDateTime(row.clockTime?.clockStart)}</Typography>
                      <Typography variant="caption" className={classes.muted}>
                        {isViewingToday && row.clockedIn ? `Elapsed ${formatElapsed(row.elapsedMinutes)}` : `Out ${formatDateTime(row.clockTime?.clockEnd)}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.route?.routeName || row.employee?.localCrew?.name || '-'}</Typography>
                      <Typography variant="caption" className={classes.muted}>{row.route?.crewLeaderName || row.employee?.localCrew?.foremanName || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.job?.propertyName || row.job?.workTicketNumber || '-'}</Typography>
                      <Typography variant="caption" className={classes.muted}>{row.job?.serviceName || row.job?.workTicketStatusName || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.warnings.length ? row.warnings.map(warning => (
                        <Chip key={warning} size="small" className={classes.warningChip} label={warning.replace(/_/g, ' ')} />
                      )) : <Typography variant="caption" className={classes.muted}>None</Typography>}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box p={4} textAlign="center">
                        <Typography>No Aspire timeclock rows match the current filters.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Drawer anchor="right" open={!!selectedRow} onClose={() => setSelectedRow(null)}>
          <Box className={classes.drawer}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{selectedRow?.employee?.displayName || selectedRow?.contactName || 'Timeclock Row'}</Typography>
              <IconButton onClick={() => setSelectedRow(null)}><CloseIcon /></IconButton>
            </Box>
            <Typography variant="body2" className={classes.muted}>
              {selectedRow?.route?.routeName || selectedRow?.employee?.localCrew?.name || 'No route'} / {selectedRow?.job?.propertyName || 'No active job'}
            </Typography>
            <Box className={classes.detailBlock}>
              {selectedRow ? JSON.stringify(selectedRow, null, 2) : ''}
            </Box>
          </Box>
        </Drawer>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

export default AspireTimeclockDashboard;
